import { Input } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { companies, type Company } from "../../api/addressBook.service";
import styles from "./CompanyPicker.module.css";

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;

type CompanyPickerProps = {
  value: Company | null;
  onChange: (company: Company | null) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  listboxId?: string;
  renderOptionAnnotation?: (company: Company) => string | null;
};

/**
 * Searchable address-book company picker. Debounces a name search against
 * `/v1/address-book/companies` and lets the user select (or clear) a company.
 * Clearing the input emits `onChange(null)`.
 */
export function CompanyPicker({
  value,
  onChange,
  label = "Company",
  placeholder = "Search address-book companies by name…",
  error,
  disabled,
  listboxId = "company-picker-listbox",
  renderOptionAnnotation,
}: CompanyPickerProps) {
  const [searchText, setSearchText] = useState(value?.name ?? "");
  const [results, setResults] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Reflect externally-supplied selections (e.g. an org's existing company on load).
  useEffect(() => {
    setSearchText(value?.name ?? "");
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    // Don't re-search the term that already matches the selected company.
    if (value && searchText === value.name) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const term = searchText.trim();
    if (term.length === 0) {
      setResults([]);
      setLoading(false);
      setSearchError(null);
      return;
    }

    setLoading(true);
    setSearchError(null);
    debounceRef.current = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      try {
        const rows = (await companies.getAll({ search: term, limit: SEARCH_LIMIT })).data;
        if (requestId !== requestIdRef.current) return;
        setResults(rows);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setResults([]);
        setSearchError("Couldn't load companies. Try again.");
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, [searchText, value]);

  function handleInputChange(next: string) {
    setSearchText(next);
    setIsOpen(true);
    if (value && next !== value.name) {
      onChange(null);
    }
  }

  function handleSelect(company: Company) {
    onChange(company);
    setSearchText(company.name);
    setResults([]);
    setIsOpen(false);
  }

  const showDropdown = isOpen && searchText.trim().length > 0;
  const selectedAnnotation = value && renderOptionAnnotation ? renderOptionAnnotation(value) : null;

  return (
    <div className={styles.picker} ref={containerRef}>
      <Input
        label={label}
        value={searchText}
        onChange={event => handleInputChange(event.target.value)}
        onFocus={() => setIsOpen(true)}
        error={error}
        disabled={disabled}
        fullWidth
        autoComplete="off"
        placeholder={placeholder}
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls={listboxId}
      />
      {selectedAnnotation && <p className={styles.selectedAnnotation}>{selectedAnnotation}</p>}
      {showDropdown && (
        <ul id={listboxId} role="listbox" className={styles.dropdown}>
          {loading && (
            <li className={styles.dropdownStatus} aria-live="polite">
              Searching…
            </li>
          )}
          {!loading && searchError && (
            <li className={styles.dropdownError} role="alert" aria-live="assertive">
              {searchError}
            </li>
          )}
          {!loading && !searchError && results.length === 0 && (
            <li className={styles.dropdownStatus} aria-live="polite">
              No companies found
            </li>
          )}
          {!loading &&
            !searchError &&
            results.map(company => {
              const annotation = renderOptionAnnotation ? renderOptionAnnotation(company) : null;
              return (
                <li
                  key={company.id}
                  role="option"
                  aria-selected={value?.id === company.id}
                  tabIndex={0}
                  className={styles.dropdownItem}
                  onClick={() => handleSelect(company)}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelect(company);
                    }
                  }}
                >
                  {company.name}
                  {annotation ? ` · ${annotation}` : ""}
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}

export default CompanyPicker;
